import { DEFAULT_TIME_ZONE } from '@crm/locale';
import { z } from 'zod';
import { SWATCH_COLORS, type SwatchColor } from '@web/shared/ui/color-swatch';
import {
  addDays,
  isCalendarDate,
  weekdayOf,
  zonedDate,
  zonedInstant,
  zonedTime,
} from '@web/shared/util/zoned-time';
import type { PersonRef, UnitRef } from '../customers/contract';

/**
 * The tasks contract (docs/specs/006-tarefas-e-atividades/spec.md, §3 and §6): the shapes the
 * API answers, the zod schemas of what it accepts and the pure rules both sides share — the
 * due instant in the organization's time zone, overdue, the tabs, snoozing and reminders.
 * The screens and the mocked API (`src/mocks/tasks`) use the same functions.
 */

/* ------------------------------------------------------------------ constants */

export const TASK_STATUSES = ['open', 'done', 'canceled'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_PRIORITIES = ['high', 'medium', 'low'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_REMINDERS = ['none', 'at-time', '15m', '1h', '1d'] as const;

/** Spec 050: how a task repeats. */
export const TASK_RECURRENCE_FREQUENCIES = ['daily', 'weekly', 'monthly'] as const;
export type TaskRecurrenceFrequency = (typeof TASK_RECURRENCE_FREQUENCIES)[number];
export interface TaskRecurrence {
  frequency: TaskRecurrenceFrequency;
  interval: number;
  until: string | null;
  /** How many more tasks after this one; null = no count. */
  count: number | null;
}

/** The repetitions the form offers (any other one shows as "Personalizada"). */
export const REPEAT_PRESETS = {
  daily: { frequency: 'daily', interval: 1 },
  weekly: { frequency: 'weekly', interval: 1 },
  biweekly: { frequency: 'weekly', interval: 2 },
  monthly: { frequency: 'monthly', interval: 1 },
  quarterly: { frequency: 'monthly', interval: 3 },
  semiannual: { frequency: 'monthly', interval: 6 },
  yearly: { frequency: 'monthly', interval: 12 },
} as const satisfies Record<string, Pick<TaskRecurrence, 'frequency' | 'interval'>>;
export type RepeatPreset = keyof typeof REPEAT_PRESETS;
export const REPEAT_OPTIONS = ['none', ...(Object.keys(REPEAT_PRESETS) as RepeatPreset[])] as const;
export type RepeatOption = RepeatPreset | 'none' | 'custom';

/** The form option of a repetition: a preset, `custom` for any other, `none` without one. */
export function repeatOptionOf(recurrence: TaskRecurrence | null | undefined): RepeatOption {
  if (!recurrence) return 'none';
  const found = (Object.keys(REPEAT_PRESETS) as RepeatPreset[]).find(
    (key) =>
      REPEAT_PRESETS[key].frequency === recurrence.frequency &&
      REPEAT_PRESETS[key].interval === recurrence.interval,
  );
  return found && !recurrence.until && recurrence.count === null ? found : 'custom';
}
export type TaskReminder = (typeof TASK_REMINDERS)[number];

export const TASK_TYPE_ICONS = [
  'call',
  'meeting',
  'follow-up',
  'whatsapp',
  'email',
  'visit',
  'task',
  'deadline',
  'document',
  'lunch',
] as const;
export type TaskTypeIcon = (typeof TASK_TYPE_ICONS)[number];

export const TASK_BUCKETS = ['today', 'overdue', 'upcoming', 'done'] as const;
export type TaskBucket = (typeof TASK_BUCKETS)[number];

export const TASK_SCOPES = ['mine', 'team'] as const;
export type TaskScope = (typeof TASK_SCOPES)[number];

/** What a task (or a note) belongs to. Conversations join in phase 3. */
export const SUBJECT_TYPES = ['customer', 'deal'] as const;
export type SubjectType = (typeof SUBJECT_TYPES)[number];

export const SNOOZE_PRESETS = ['1h', 'tomorrow', 'next-monday'] as const;
export type SnoozePreset = (typeof SNOOZE_PRESETS)[number];

/** The organization's zone when it has none (the API's default too). */
export const ORGANIZATION_TIME_ZONE = DEFAULT_TIME_ZONE;

/** All-day tasks remind from this time of their day (spec rule 8). */
export const ALL_DAY_REMINDER_TIME = '09:00';

/** The calendar draws a task with a time and no duration with this length. */
export const DEFAULT_DURATION_MINUTES = 30;

export const TASK_PAGE_SIZE = 50;
export const TASK_MAX_PAGE_SIZE = 200;

export const TASK_LIMITS = {
  titleMin: 2,
  titleMax: 160,
  descriptionMax: 2000,
  resultMax: 1000,
  durationMin: 5,
  durationMax: 1440,
  typeNameMin: 2,
  typeNameMax: 40,
} as const;

/* ------------------------------------------------------------------ shapes */

export interface TaskTypeRef {
  id: string;
  name: string;
  icon: TaskTypeIcon;
  color: SwatchColor;
}

export interface TaskType extends TaskTypeRef {
  active: boolean;
  order: number;
  createdAt: string;
  updatedAt: string;
}

export interface SubjectRef {
  type: SubjectType;
  id: string;
  /** The customer's name or the deal's title. */
  label: string;
}

export interface Task {
  id: string;
  title: string;
  type: TaskTypeRef;
  /** `YYYY-MM-DD`, in the organization's time zone. */
  dueDate: string;
  /** `HH:MM`, or `null` for an all-day task. */
  dueTime: string | null;
  /** The instant it starts (00:00 of the day for an all-day task). */
  dueAt: string;
  durationMinutes: number | null;
  assignee: PersonRef;
  unit: UnitRef;
  subject: SubjectRef | null;
  /** The conversation of the inbox the task was created from. */
  conversationId: string | null;
  /** A demand: the WhatsApp group message it was opened from (spec 025). */
  sourceMessageId?: string | null;
  priority: TaskPriority;
  description: string | null;
  reminder: TaskReminder;
  reminderAt: string | null;
  /** Spec 050: how the task repeats (absent on an older API). */
  recurrence?: TaskRecurrence | null;
  status: TaskStatus;
  overdue: boolean;
  completedAt: string | null;
  completedBy: PersonRef | null;
  result: string | null;
  createdBy: PersonRef | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskList {
  items: Task[];
  total: number;
  page: number;
  pageSize: number;
}

export type TaskCounts = Record<TaskBucket, number>;

export interface TeamTaskSummary {
  members: { assignee: PersonRef; overdue: number; today: number }[];
}

/** The next activity of a deal or a customer (spec rule 17). */
export interface NextTask {
  id: string;
  title: string;
  dueDate: string;
  dueTime: string | null;
  overdue: boolean;
  type: TaskTypeRef;
}

export interface CompleteTaskResult {
  task: Task;
  next: Task | null;
}

/* ------------------------------------------------------------------ schemas */

const isoDate = z.string().refine(isCalendarDate, { message: 'Invalid date' });
const wallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === '' ? null : value));

export const subjectInputSchema = z
  .object({ type: z.enum(SUBJECT_TYPES), id: z.string().min(1) })
  .strict();

export const taskTypeInputSchema = z
  .object({
    name: z.string().trim().min(TASK_LIMITS.typeNameMin).max(TASK_LIMITS.typeNameMax),
    icon: z.enum(TASK_TYPE_ICONS),
    color: z.enum(SWATCH_COLORS),
    active: z.boolean().optional(),
  })
  .strict();
export type TaskTypeInput = z.input<typeof taskTypeInputSchema>;

export const updateTaskTypeSchema = taskTypeInputSchema.partial().strict();
export type UpdateTaskTypeInput = z.input<typeof updateTaskTypeSchema>;

export const reorderTaskTypesSchema = z.object({ ids: z.array(z.string().min(1)).min(1) }).strict();

const taskFields = {
  title: z.string().trim().min(TASK_LIMITS.titleMin).max(TASK_LIMITS.titleMax),
  typeId: z.string().min(1),
  dueDate: isoDate,
  dueTime: wallTime.nullable().optional(),
  durationMinutes: z
    .number()
    .int()
    .min(TASK_LIMITS.durationMin)
    .max(TASK_LIMITS.durationMax)
    .nullable()
    .optional(),
  assigneeMembershipId: z.string().min(1).optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  description: optionalText(TASK_LIMITS.descriptionMax).optional(),
  reminder: z.enum(TASK_REMINDERS).optional(),
  /** Spec 050: null stops the repetition. */
  recurrence: z
    .object({
      frequency: z.enum(TASK_RECURRENCE_FREQUENCIES),
      interval: z.number().int().min(1).max(99),
      until: isoDate.nullable().optional(),
      count: z.number().int().min(1).max(999).nullable().optional(),
    })
    .nullable()
    .optional(),
};

/** A duration needs a time (rule 4). */
const durationNeedsTime = (task: {
  dueTime?: string | null | undefined;
  durationMinutes?: number | null | undefined;
}) => !(task.durationMinutes != null && !task.dueTime);

const DURATION_ISSUE = { message: 'A duration needs a time', path: ['durationMinutes'] };

export const createTaskSchema = z
  .object({
    ...taskFields,
    subject: subjectInputSchema.nullable().optional(),
    conversationId: z.string().min(1).nullable().optional(),
    /** A demand: the group message it is opened from (spec 025). */
    sourceMessageId: z.string().min(1).nullable().optional(),
  })
  .strict()
  .refine(durationNeedsTime, DURATION_ISSUE);
export type CreateTaskInput = z.input<typeof createTaskSchema>;

/** The checks of the whole task run on the merged result (an edit may send one field). */
export const updateTaskSchema = z.object(taskFields).partial().strict();
export type UpdateTaskInput = z.input<typeof updateTaskSchema>;

export const completeTaskSchema = z
  .object({
    result: optionalText(TASK_LIMITS.resultMax).optional(),
    next: z
      .object({ ...taskFields, subject: subjectInputSchema.nullable().optional() })
      .strict()
      .refine(durationNeedsTime, DURATION_ISSUE)
      .optional(),
    /** A demand: tell its group "✅ Resolvido" (spec 025, rule 7). */
    notifyGroup: z.boolean().optional(),
  })
  .strict();
export type CompleteTaskInput = z.input<typeof completeTaskSchema>;

export const snoozeTaskSchema = z.union([
  z.object({ preset: z.enum(SNOOZE_PRESETS) }).strict(),
  z.object({ dueDate: isoDate, dueTime: wallTime.nullable().optional() }).strict(),
]);
export type SnoozeTaskInput = z.input<typeof snoozeTaskSchema>;

export const taskListQuerySchema = z.object({
  scope: z.enum(TASK_SCOPES).optional(),
  bucket: z.enum(TASK_BUCKETS).optional(),
  status: z.enum(TASK_STATUSES).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
  typeId: z.string().min(1).optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  assigneeMembershipId: z.string().min(1).optional(),
  subjectType: z.enum([...SUBJECT_TYPES, 'none']).optional(),
  subjectId: z.string().min(1).optional(),
  page: z.coerce.number().int().min(1).optional(),
  pageSize: z.coerce.number().int().min(1).max(TASK_MAX_PAGE_SIZE).optional(),
});

export interface TaskListQuery {
  scope?: TaskScope;
  bucket?: TaskBucket;
  status?: TaskStatus;
  from?: string;
  to?: string;
  typeId?: string;
  priority?: TaskPriority;
  assigneeMembershipId?: string;
  subjectType?: SubjectType | 'none';
  subjectId?: string;
  page?: number;
  pageSize?: number;
}

export type TaskCountsQuery = Omit<
  TaskListQuery,
  'bucket' | 'status' | 'from' | 'to' | 'page' | 'pageSize'
>;

/* ------------------------------------------------------------------ rules */

/** Today in the organization's time zone. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return zonedDate(now, timeZone);
}

/** The instant a task starts: its time, or 00:00 of its day (rule 4). */
export function taskDueAt(dueDate: string, dueTime: string | null, timeZone: string): string {
  return zonedInstant(dueDate, dueTime, timeZone).toISOString();
}

/** An open task past its due date — or, with a time, past its instant (rule 11). */
export function isTaskOverdue(
  task: { status: TaskStatus; dueDate: string; dueTime: string | null },
  timeZone: string,
  now: Date = new Date(),
): boolean {
  if (task.status !== 'open') return false;
  const today = todayIn(timeZone, now);
  if (task.dueDate !== today) return task.dueDate < today;
  if (!task.dueTime) return false;
  return zonedInstant(task.dueDate, task.dueTime, timeZone).getTime() < now.getTime();
}

/** The tab a task falls in (rule 12); canceled tasks fall in none. */
export function taskBucket(
  task: { status: TaskStatus; dueDate: string },
  timeZone: string,
  now: Date = new Date(),
): TaskBucket | null {
  if (task.status === 'done') return 'done';
  if (task.status !== 'open') return null;
  const today = todayIn(timeZone, now);
  if (task.dueDate < today) return 'overdue';
  return task.dueDate === today ? 'today' : 'upcoming';
}

const REMINDER_LEAD_MINUTES: Record<Exclude<TaskReminder, 'none'>, number> = {
  'at-time': 0,
  '15m': 15,
  '1h': 60,
  '1d': 24 * 60,
};

/** When the reminder fires (rule 8): all-day tasks count from 09:00 of their day. */
export function reminderInstant(
  task: { dueDate: string; dueTime: string | null; reminder: TaskReminder },
  timeZone: string,
): string | null {
  if (task.reminder === 'none') return null;
  const start = zonedInstant(task.dueDate, task.dueTime ?? ALL_DAY_REMINDER_TIME, timeZone);
  return new Date(start.getTime() - REMINDER_LEAD_MINUTES[task.reminder] * 60_000).toISOString();
}

/**
 * The new due date and time of a snoozed task (rule 10): counted from today in the
 * organization's zone, keeping the task's time; "in 1 hour" gives it one.
 */
export function snoozeTarget(
  preset: SnoozePreset,
  current: { dueTime: string | null },
  timeZone: string,
  now: Date = new Date(),
): { dueDate: string; dueTime: string | null } {
  const today = todayIn(timeZone, now);
  switch (preset) {
    case '1h': {
      const fiveMinutes = 5 * 60_000;
      const target = new Date(Math.ceil((now.getTime() + 60 * 60_000) / fiveMinutes) * fiveMinutes);
      return { dueDate: zonedDate(target, timeZone), dueTime: zonedTime(target, timeZone) };
    }
    case 'tomorrow':
      return { dueDate: addDays(today, 1), dueTime: current.dueTime };
    case 'next-monday': {
      const weekday = weekdayOf(today);
      const ahead = (8 - weekday) % 7 || 7;
      return { dueDate: addDays(today, ahead), dueTime: current.dueTime };
    }
  }
}

/** Order of open tasks: by day, all-day ones first, then by time (rule 12). */
export function compareByDue(
  a: { dueDate: string; dueTime: string | null },
  b: { dueDate: string; dueTime: string | null },
): number {
  if (a.dueDate !== b.dueDate) return a.dueDate.localeCompare(b.dueDate);
  if (a.dueTime === b.dueTime) return 0;
  if (a.dueTime === null) return -1;
  if (b.dueTime === null) return 1;
  return a.dueTime.localeCompare(b.dueTime);
}

/** The default title of the next task, from the completed one. */
export function nextTaskTitle(title: string, prefix: string): string {
  const text = `${prefix}: ${title}`;
  return text.length > TASK_LIMITS.titleMax ? text.slice(0, TASK_LIMITS.titleMax) : text;
}
